import { type BlockButtons, ChatContainerTypes, ChatCustomBlockTypes } from "@do/shared-types";
import { getEffectiveButtonDelaySeconds } from "./button-delays";
import { type CombinedItem } from "./combine-blocks-and-containers";

type CombinedContainer = Extract<CombinedItem, { blocks: unknown[] }>;
type CombinedBlock = Exclude<CombinedItem, CombinedContainer>;

const isCombinedContainer = (item: CombinedItem): item is CombinedContainer =>
  Boolean(item) &&
  typeof item === "object" &&
  "blocks" in item &&
  Array.isArray((item as { blocks?: unknown }).blocks);

// Buttons from several blocks end up in a single flat list, so the block delay
// has to be carried over to each button before that grouping is lost.
const applyBlockDelay = (block: BlockButtons) => {
  const blockDelay = block.delay ?? 0;
  if (blockDelay <= 0) return block.buttons;

  return block.buttons.map((button) => ({
    ...button,
    delay: getEffectiveButtonDelaySeconds(button.delay, blockDelay),
  }));
};

export const aggregateBlocks = (items: CombinedItem[] | undefined) => {
  const acc = {
    // Keep containers as-is (do not traverse them here).
    blocks: [] as CombinedItem[],
    buttons: [] as BlockButtons["buttons"],
    grid: null as CombinedContainer | null,
    carousel: null as CombinedContainer | null,
  };

  for (const item of items ?? []) {
    // Important: do NOT visit container children here.
    // We keep the container itself in `blocks` so the caller can render it.
    if (isCombinedContainer(item)) {
      if (item.type === ChatContainerTypes.grid) {
        acc.grid = item;
      } else if (item.type === ChatContainerTypes.carousel) {
        acc.carousel = item;
      } else {
        acc.blocks.push(item);
      }
      continue;
    }

    const block = item as CombinedBlock;
    if (
      block.type === ChatCustomBlockTypes.buttons &&
      "buttons" in block &&
      Array.isArray(block.buttons) &&
      block.buttons.length > 0
    ) {
      acc.buttons.push(...applyBlockDelay(block));
    } else {
      // Remaining top-level blocks are kept for rendering.
      acc.blocks.push(block);
    }
  }

  return acc;
};
